import { Component, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, UntypedFormControl, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { MatStepper } from '@angular/material/stepper';
import { Router } from '@angular/router';
import { Client } from '../../../projects/kiene-core/src/lib/components/client/classes/client';
import { Betriebsart } from '../../../projects/kiene-core/src/lib/components/stammdaten/vvvo/classes/betriebsart';
import { KieneUtils } from '../../../projects/kiene-core/src/lib/utils/KieneUtils';
import { API_BASE_URL_SERVICE } from '../../../projects/kiene-core/src/lib/kiene-core.config';
import { KieneBackendApiService } from '../../../projects/kiene-core/src/lib/services/backend-api/kiene-backend-api.service';
import { KieneSession } from '../../../projects/kiene-core/src/lib/services/local-storage/kiene-session';
import { LocalStorageService } from '../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { MessageService } from '../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { KieneStringUtils } from '../../../projects/kiene-core/src/lib/utils/KieneStringUtils';
import { handleEnterKeyForOverlayDialog } from '../../../projects/kiene-core/src/lib/utils/enter-key-dialog.util';
import { InitialSetupService } from '../initial-setup/initial-setup.service';
import { RegisterService } from './register.service';
import { LoginService } from '../../../projects/kiene-core/src/lib/services/user/login.service';

export class HitLoginDetails {
    vvvo: string;
    pin: string;

    constructor() {}
}

export class HitLoginResponse extends Client {
    benutzer_email: string;
    benutzer_vorname: string;
    benutzer_nachname: string;
    landwirt: number;
    viehhaendler: number;
}

@Component({
    selector: 'kiene-register',
    templateUrl: './register.component.html',
    styleUrls: ['./register.component.scss'],
})
export class RegisterComponent implements OnInit {
    // Passwort-Konfiguration
    readonly MIN_PASSWORD_LENGTH = 8;

    @ViewChild('stepper') stepper: MatStepper;

    @HostListener('document:keydown.enter', ['$event'])
    handleEnter(event: KeyboardEvent) {
        if (handleEnterKeyForOverlayDialog(event)) {
            return;
        }
        event.preventDefault();
        if (!this.hasHitLoggedIn) {
            this.hitLogin();
            return;
        }
        if (this.stepper.selectedIndex === 0) {
            this.validateAndGoToStep2();
        } else if (this.stepper.selectedIndex === 1) {
            this.validateAndGoToStep3();
        } else if (this.stepper.selectedIndex === 2) {
            this.register();
        }
    }

    // hit
    @ViewChild('hitPinInput') hitPinInput: MatInput;
    hitVvvoCtrl: FormControl = new FormControl('276111111111111', [Validators.required, Validators.maxLength(15)]);
    hitPinCtrl: FormControl = new FormControl('12345', Validators.required);
    hidePassword = true;
    hasHitLoggedIn = false;
    isLoading = false;
    hitRequest: HitLoginDetails;
    hitLoginResponse: HitLoginResponse;

    // vetvet
    betriebsarten: Betriebsart[] = [];
    betriebsartCtrl = new FormControl(null, Validators.required);
    agbCtrl = new FormControl(undefined, [Validators.requiredTrue]);
    passwortCtrl: UntypedFormControl = new UntypedFormControl(null);
    passwortWdhCtrl: UntypedFormControl = new UntypedFormControl(null);

    hitEmailCtrl: UntypedFormControl = new UntypedFormControl('', [Validators.required, Validators.email]);
    benutzerEmailEmptyInital = false;
    benutzerEmailCtrl: UntypedFormControl = new UntypedFormControl('', Validators.required);
    landwirtHaendlerCtrl = new FormControl(0, [Validators.required]);

    // FormGroups für Stepper-Validierung
    step1FormGroup: FormGroup;
    step2FormGroup: FormGroup;
    step3FormGroup: FormGroup;

    // FormControls für die Validierung
    vornameCtrl = new FormControl('', [Validators.required]);
    nachnameCtrl = new FormControl('', [Validators.required]);
    firmennameCtrl = new FormControl('', [Validators.required]);
    strasseCtrl = new FormControl('', [Validators.required]);
    plzCtrl = new FormControl('', [Validators.required, Validators.pattern(/^\d{5}$/)]);
    ortCtrl = new FormControl('', [Validators.required]);
    telefonnummerCtrl: FormControl;
    handynummerCtrl: FormControl;

    sgsNummerCtrl = new FormControl('', []); // SGS Nummer Control

    constructor(
        private loginService: LoginService,
        private localStorageService: LocalStorageService,
        private registerService: RegisterService,
        private messageService: MessageService,
        private initialSetupService: InitialSetupService,
        private router: Router,
        @Inject(API_BASE_URL_SERVICE) private apiBaseUrl: string,
        private api: KieneBackendApiService,
    ) {
        if (this.localStorageService.isLoggedIn()) {
            this.loginService.logout().subscribe(() => {
                this.localStorageService.deleteBrowserSession('constructor RegisterComponent');
            });
        }

        // Passwort-Validatoren
        this.passwortCtrl = new UntypedFormControl(null, [
            Validators.required,
            Validators.minLength(this.MIN_PASSWORD_LENGTH),
            this.passwordValidator(),
        ]);
        this.passwortWdhCtrl = new UntypedFormControl(null, [
            Validators.required,
            // passwordMatchValidator wird auf FormGroup-Ebene angewendet
        ]);

        // FormGroups initialisieren
        this.step1FormGroup = new FormGroup({ landwirtHaendler: this.landwirtHaendlerCtrl });

        this.step2FormGroup = new FormGroup(
            {
                vorname: this.vornameCtrl,
                nachname: this.nachnameCtrl,
                email: this.benutzerEmailCtrl,
                passwort: this.passwortCtrl,
                passwortWdh: this.passwortWdhCtrl,
                agb: this.agbCtrl,
            },
            [RegisterComponent.passwordsMatch],
        ); // Static Validator auf FormGroup-Ebene

        // Telefonnummer FormControls mit Pattern-Validierung
        this.telefonnummerCtrl = new FormControl('', [
            Validators.pattern(/^(0|\+49|0049)\s?[1-9][0-9\s\-]{5,15}[0-9]$/),
        ]);

        this.handynummerCtrl = new FormControl('', [
            Validators.pattern(/^(0|\+49|0049)(15[0-9]|16[0-9]|17[0-9])[0-9\s\-]{7,9}$/),
        ]);

        // FormGroup für Schritt 3 erweitern
        this.step3FormGroup = new FormGroup({
            firmenname: this.firmennameCtrl,
            email: this.hitEmailCtrl,
            betriebsart: this.betriebsartCtrl,
            strasse: this.strasseCtrl,
            plz: this.plzCtrl,
            ort: this.ortCtrl,
            telefonnummer: this.telefonnummerCtrl,
            handynummer: this.handynummerCtrl,
        });
    }

    // Static Password Match Validator
    static passwordsMatch(group: AbstractControl): { [key: string]: any } | null {
        const password = group.get('passwort')?.value;
        const confirmPassword = group.get('passwortWdh')?.value;

        return password === confirmPassword ? null : { passwordsNotMatch: true };
    }

    // Passwort-Validator
    passwordValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            const value = control.value;
            if (!value) return null;

            let errors: any = {};
            if (!this.hasUppercase(value)) errors.uppercase = true;
            if (!this.hasLowercase(value)) errors.lowercase = true;
            if (!this.hasNumber(value)) errors.number = true;

            return Object.keys(errors).length > 0 ? errors : null;
        };
    }

    // Hilfsmethoden für Passwort-Validierung
    hasUppercase(password: string): boolean {
        return password ? /[A-Z]/.test(password) : false;
    }

    hasLowercase(password: string): boolean {
        return password ? /[a-z]/.test(password) : false;
    }

    hasNumber(password: string): boolean {
        return password ? /[0-9]/.test(password) : false;
    }

    ngOnInit(): void {
        this.betriebsartCtrl.valueChanges.subscribe((betriebsart: Betriebsart | null) => {
            this.syncBetriebsartToHitLoginResponse(betriebsart);
            if (betriebsart?.bezeichnung === 'Mastkälber') {
                this.sgsNummerCtrl.setValidators([Validators.pattern(/^\d+$/)]);
                if (!this.step3FormGroup.contains('sgsNummer')) {
                    this.step3FormGroup.addControl('sgsNummer', this.sgsNummerCtrl);
                }
            } else {
                this.sgsNummerCtrl.setValue('');
                this.sgsNummerCtrl.clearValidators();
                this.sgsNummerCtrl.updateValueAndValidity();
                if (this.step3FormGroup.contains('sgsNummer')) {
                    this.step3FormGroup.removeControl('sgsNummer');
                }
            }
            this.sgsNummerCtrl.updateValueAndValidity();
        });

        this.landwirtHaendlerCtrl.valueChanges.subscribe({
            next: value => {
                if (value === 1) {
                    this.ladeBetriebsartViehhaendler();
                }
            },
        });

        this.hitVvvoCtrl.valueChanges.subscribe({
            next: (value: string) => {
                if (value && value.length === 15) {
                    this.hitPinInput.focus();
                }
            },
        });

        this.vornameCtrl.valueChanges.subscribe(value => {
            if (this.hitLoginResponse) {
                this.hitLoginResponse.benutzer_vorname = value;
            }
        });

        this.nachnameCtrl.valueChanges.subscribe(value => {
            if (this.hitLoginResponse) {
                this.hitLoginResponse.benutzer_nachname = value;
            }
        });

        this.firmennameCtrl.valueChanges.subscribe(value => {
            if (this.hitLoginResponse) {
                this.hitLoginResponse.bezeichnung = value;
            }
        });

        this.strasseCtrl.valueChanges.subscribe(value => {
            if (this.hitLoginResponse) {
                this.hitLoginResponse.strasse = value;
            }
        });

        this.plzCtrl.valueChanges.subscribe(value => {
            if (this.hitLoginResponse) {
                this.hitLoginResponse.plz = value;
            }
        });

        this.ortCtrl.valueChanges.subscribe(value => {
            if (this.hitLoginResponse) {
                this.hitLoginResponse.ort = value;
            }
        });

        this.telefonnummerCtrl.valueChanges.subscribe(value => {
            if (this.hitLoginResponse) {
                this.hitLoginResponse.telefon_1 = value;
            }
        });

        this.handynummerCtrl.valueChanges.subscribe(value => {
            if (this.hitLoginResponse) {
                this.hitLoginResponse.mobil_1 = value;
            }
        });

        this.sgsNummerCtrl.valueChanges.subscribe(value => {
            if (this.hitLoginResponse) {
                this.hitLoginResponse.sgs_nummer = value;
            }
        });
    }

    hitFormValid(): boolean {
        let valid = true;

        this.hitVvvoCtrl.markAsTouched();
        valid = valid && this.hitVvvoCtrl.valid;

        this.hitPinCtrl.markAsTouched();
        valid = valid && this.hitPinCtrl.valid;

        return valid;
    }

    hitLogin() {
        if (this.hitFormValid()) {
            this.isLoading = true;
            this.hitRequest = new HitLoginDetails();
            this.hitRequest.vvvo = this.hitVvvoCtrl.value;
            this.hitRequest.pin = this.hitPinCtrl.value;

            this.registerService.hitLogin(this.hitRequest).subscribe({
                next: (hitLoginResponse: HitLoginResponse) => {
                    console.log('hitLoginResponse', hitLoginResponse);
                    this.hasHitLoggedIn = true;
                    this.hitLoginResponse = hitLoginResponse;
                    this.isLoading = false;
                    this.initialSetupService.setHasHitLoggedIn(true);
                    this.initVetVetRegistration();
                },
                error: error => {
                    this.isLoading = false;
                    this.messageService.errorMessage('Fehler beim Laden der HIT-Daten!', error);
                },
            });
        } else {
            this.messageService.alertMessage('Eingaben nicht vollständig!');
        }
    }

    public compareBetriebsarten(b1: Betriebsart, b2: Betriebsart) {
        return KieneUtils.sameId(b1?.betriebsart_id, b2?.betriebsart_id);
    }

    /**
     * Update Benutzer email, wenn diese am Anfang nicht gesetzt war, anhand der hit-Email und dessen Aenderungen
     */
    syncEmailInputs() {
        this.benutzerEmailEmptyInital = KieneStringUtils.isNullOrEmpty(this.hitLoginResponse.benutzer_email);

        this.hitEmailCtrl.setValue(this.hitLoginResponse.email);
        this.benutzerEmailCtrl.setValue(this.hitLoginResponse.benutzer_email);

        this.hitEmailCtrl.valueChanges.subscribe(value => {
            this.hitLoginResponse.email = value;
            if (this.benutzerEmailEmptyInital) {
                this.benutzerEmailCtrl.setValue(value);
                this.hitLoginResponse.benutzer_email = value;
            }
        });

        this.benutzerEmailCtrl.valueChanges.subscribe(value => {
            this.hitLoginResponse.benutzer_email = value;
            if (!KieneStringUtils.isNullOrEmpty(value)) {
                this.benutzerEmailEmptyInital = false;
            }
        });
    }

    register() {
        // Mark all FormGroups as touched to show validation errors
        this.markFormGroupTouched(this.step1FormGroup);
        this.markFormGroupTouched(this.step2FormGroup);
        this.markFormGroupTouched(this.step3FormGroup);

        // Check if all steps are valid
        const allStepsValid = this.step1FormGroup.valid && this.step2FormGroup.valid && this.step3FormGroup.valid;

        this.hitLoginResponse.landwirt = this.landwirtHaendlerCtrl.value === 0 ? 1 : 0;
        this.hitLoginResponse.viehhaendler = this.landwirtHaendlerCtrl.value === 1 ? 1 : 0;
        this.syncBetriebsartToHitLoginResponse();

        if (allStepsValid) {
            this.isLoading = true;
            this.registerService
                .register(
                    this.hitLoginResponse,
                    this.passwortCtrl.value,
                    this.passwortWdhCtrl.value,
                    this.hitRequest.pin,
                )
                .subscribe({
                    next: resp => {
                        const newSession = <KieneSession>resp;
                        this.localStorageService.setCurrentUser(newSession);
                        this.localStorageService.setCurrentMandant(
                            // TODO: Mandant nicht default setzen
                            newSession.mandanten[0],
                        );
                        this.isLoading = false;
                        let skipInitialSetup = false;
                        for (const ur of newSession.benutzerrollen) {
                            if (ur.viehhaendler === 1) {
                                skipInitialSetup = true;
                            }
                        }
                        if (skipInitialSetup) {
                            this.router.navigate(['/']).then();
                        } else {
                            this.router.navigate(['initial-setup']).then();
                        }
                        this.messageService.infoMessage('Erfolgreich registriert.');
                    },
                    error: error => {
                        this.isLoading = false;
                        this.messageService.errorMessage('Fehler beim Registrieren.', error);
                    },
                });
        } else {
            this.messageService.alertMessage('Nicht alle notwendigen Eingabefelder ausgefüllt!');
            // Navigiere zum ersten fehlerhaften Schritt
            this.navigateToFirstInvalidStep();
        }
    }

    private navigateToFirstInvalidStep() {
        if (!this.step1FormGroup.valid) {
            this.stepper.selectedIndex = 0;
            this.markFormGroupTouched(this.step1FormGroup);
        } else if (!this.step2FormGroup.valid) {
            this.stepper.selectedIndex = 1;
            this.markFormGroupTouched(this.step2FormGroup);
        } else if (!this.step3FormGroup.valid) {
            this.stepper.selectedIndex = 2;
            this.markFormGroupTouched(this.step3FormGroup);
        }
    }

    backToLogin() {
        this.router.navigate(['/login']).then();
    }

    private initVetVetRegistration() {
        this.syncEmailInputs();
        this.loadBetriebsarten();
        this.syncFormControlsWithData();
    }

    private syncFormControlsWithData() {
        // Daten von HIT-Response in FormControls setzen
        if (this.hitLoginResponse) {
            this.vornameCtrl.setValue(this.hitLoginResponse.benutzer_vorname || '');
            this.nachnameCtrl.setValue(this.hitLoginResponse.benutzer_nachname || '');
            this.firmennameCtrl.setValue(this.hitLoginResponse.bezeichnung || '');
            this.hitEmailCtrl.setValue(this.hitLoginResponse.email || '');
            this.strasseCtrl.setValue(this.hitLoginResponse.strasse || '');
            this.plzCtrl.setValue(this.hitLoginResponse.plz || '');
            this.ortCtrl.setValue(this.hitLoginResponse.ort || '');
            this.telefonnummerCtrl.setValue(this.hitLoginResponse.telefon_1 || '');
            this.handynummerCtrl.setValue(this.hitLoginResponse.mobil_1 || '');
        }
    }

    private syncBetriebsartToHitLoginResponse(betriebsart?: Betriebsart | null): void {
        if (!this.hitLoginResponse) {
            return;
        }
        const selected = betriebsart ?? (this.betriebsartCtrl.value as Betriebsart | null);
        if (selected?.betriebsart_id) {
            this.hitLoginResponse.betriebsart_id = selected.betriebsart_id;
            this.hitLoginResponse.betriebsart = selected.bezeichnung;
        }
    }

    private loadBetriebsarten() {
        this.registerService.loadBetriebsarten().subscribe({
            next: response => {
                this.betriebsarten = response.records;
                if (this.hitLoginResponse.betriebsart_id) {
                    const betriebsart = this.betriebsarten.find(
                        b => b.betriebsart_id === this.hitLoginResponse.betriebsart_id,
                    );
                    this.betriebsartCtrl.setValue(betriebsart);
                } else {
                    this.betriebsartCtrl.setErrors({ required: true });
                    this.betriebsartCtrl.markAsTouched();
                }
            },
        });
    }

    private ladeBetriebsartViehhaendler() {
        this.api.get(this.apiBaseUrl + 'betriebsart/read_viehhaendler.php', undefined).subscribe({
            next: betriebsart => {
                this.betriebsartCtrl.setValue(betriebsart);
            },
            error: () => {},
        });
    }

    // Helper method to mark all controls in a FormGroup as touched
    private markFormGroupTouched(formGroup: FormGroup): void {
        Object.keys(formGroup.controls).forEach(key => {
            const control = formGroup.get(key);
            control?.markAsTouched();
        });
    }

    // Validierungsmethoden mit FormGroup-basierter Validierung
    validateAndGoToStep2(): void {
        if (this.step1FormGroup.valid) {
            this.stepper.next();
            setTimeout(() => this.scrollToNextStep(), 200);
        } else {
            this.markFormGroupTouched(this.step1FormGroup);
        }
    }

    validateAndGoToStep3(): void {
        if (this.step2FormGroup.valid) {
            this.stepper.next();
            setTimeout(() => this.scrollToNextStep(), 200);
        } else {
            this.markFormGroupTouched(this.step2FormGroup);
        }
    }

    // Method to handle user type selection from cards
    selectUserType(value: number): void {
        this.landwirtHaendlerCtrl.setValue(value);
    }

    scrollToNextStep(): void {
        if (this.stepper) {
            // Finde den Step-Header basierend auf dem aktuellen selectedIndex
            const currentStepIndex = this.stepper.selectedIndex;
            const stepHeaders = document.querySelectorAll('.mat-step-header');

            if (stepHeaders && stepHeaders[currentStepIndex]) {
                stepHeaders[currentStepIndex].scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
            }
        }
    }

    goToPreviousStep(): void {
        // Kleine Verzögerung um sicherzustellen, dass der Stepper gewechselt hat
        setTimeout(() => {
            if (this.stepper) {
                const currentStepIndex = this.stepper.selectedIndex;
                const stepHeaders = document.querySelectorAll('.mat-step-header');

                if (stepHeaders && stepHeaders[currentStepIndex]) {
                    stepHeaders[currentStepIndex].scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                        inline: 'nearest',
                    });
                }
            }
        }, 100);
    }

    scrollToCurrentStep(): void {
        this.scrollToNextStep();
    }

    scrollToTop(): void {
        this.scrollToNextStep();
    }
}
